// @flow

import _ from 'lodash';
import uuidv1 from 'uuid/v1';

import {takeFilename, strip_end_slash} from './utils.js';

import Role, {AsesorExterno} from './roles.js';

import type {T_Kind} from './codtype.js';
import type {T_Role} from './roles.js';

const moment = require('moment');

require('moment/locale/es');


export type T_Ext = 'dir' | 'shp' | 'png' | 'obs' | 'sensor';


export type T_Node = {  // cuando consultamos con fetch_dir, todo lo que sabemos sobre un archivo es esto:
  Path  : string,
  File  : string,
  Type  : T_Ext,
  Unpub?: boolean,
  Days? : number
};


export type T_Item = {  // get_order() transforma un T_Node en un T_Item
  Path : string,
  File : string,
  Type : T_Ext,
  label: string,
  order: number,
  Cod  : T_Kind,
  Unpub: boolean,
  Days : number
};


export type T_User = {
  id         : number,
  user_name  : string,
  real_name  : string,
  pass       : string,
  id_rol     : number,
  role       : T_Role,
  email      : string,
  session_id : string,
  dir        : string,
  true_dirs  : Array<string>,
  true_exists: Array<string>,
  black_dirs : Array<string>
};



export function same_user_id(u1: T_User, u2: T_User): boolean {     // determina si el id y el user_name son los mismos en u1 y u2
  return (
    u1.id        === u2.id &&
    u1.user_name === u2.user_name
  );
}



export function user_is_changed(u1: T_User, u2: T_User): boolean {    // determina si algo cambió entre u1 y u2.
  return (                                                            // los números de sesión no se comparan.
    u1.real_name   !== u2.real_name               ||                  // Se asume que ya se hizo la comprobación same_user_id()
    u1.pass        !== u2.pass                    ||
    u1.id_rol      !== u2.id_rol                  ||
    u1.role        !== u2.role                    ||
    u1.email       !== u2.email                   ||
    u1.dir         !== u2.dir                     ||
    (! _.isEqual(u1.true_dirs  , u2.true_dirs  )) ||
    (! _.isEqual(u1.true_exists, u2.true_exists)) ||
    (! _.isEqual(u1.black_dirs , u2.black_dirs ))
  );
}


export type T_Next = (result: boolean) => void;


export type T_NextNum = (result: number) => void;   // NOTE: también hay un T_NextErrNum, por si acaso.


export type T_LL = {
  lat: number,
  lng: number
};


export type T_Point = [number, number];


export type T_Bounds = {
  min_bound: T_Point,
  max_bound: T_Point,
  utm_zone?: string,
}


export type T_MavRow = {
  desde: number,
  hasta: number,
  dosis: number,
  area : number,
  total: number,
  color: string
};


export type T_MavDetail = {
  path: string,
  rows: Array<T_MavRow>
};


export type T_MavDetailRaw = {
  ranges   : Array<number>,
  colors   : Array<string>,
  vdosis   : Array<number>,
  areas    : Array<number>,    // NOTE: el largo de este array es distinto al de las dosis o los colores.
  zonas    : Array<number>,    // areas[0] es de tipo zonas[0], areas[1] es de tipo zonas[1], etc
  createdBy: string
};



function total_area_zona(det: T_MavDetailRaw, zona: number): number {
  return _.sum(_.zip(det.areas, det.zonas).filter(az => az[1] === zona).map(az => az[0]));
}



export function get_mav_details(det: T_MavDetailRaw, mav_path: string): T_MavDetail {
  return {
    path: mav_path,
    rows: _.zip(_.initial(_.zip(det.ranges, _.tail(det.ranges))), det.colors, det.vdosis).map((rcd, num) => {
      const area = total_area_zona(det, num + 1);
      
      return {
        desde: rcd[0][0],
        hasta: rcd[0][1],
        dosis: rcd[2],
        area,
        total: area * rcd[2],
        color: rcd[1],
      };
    })
  };
}



export function is_empty_mav(mav_detail: ?T_MavDetail): boolean {
  if (mav_detail == null) {
    return true;
  }
  else {
    return mav_detail.path === '' || mav_detail.rows.length === 0;
  }
}


export type T_ErrStr = {error: string} | string;


export type T_ErrNum = {error: string} | number;


export type T_ErrOk = {error: string} | {ok: string};


export type T_Err = {error: string};


export type T_Range = [number, number];


export type T_NextErrOk = (x: T_ErrOk) => void;


export type T_NextPath = (x: string) => void;


export type T_NextPaths = (x: Array<string>) => void;


export type T_NextMavDetail = (x: ?T_MavDetail) => void;


export type T_NextErrStr = (x: T_ErrStr) => void;


export type T_NextErrNum = (x: T_ErrNum) => void;


export type T_EscalaLyr = {
  pathEscala: string,           // path completo del lyr.txt
  realName  : string,
  classes   : number,
  legends   : Array<string>,
  ranges    : Array<number>,
  inverted  : boolean,
  elemRange : T_Range,
  trueRanges: Array<T_Range>,
  post_inv  : boolean,
  units     : string,
  rgb_colors: Array<string>
};


export type T_ElemLyrPng = [string, string, string];    // elem_name, lyr_filename, png_filename


export type T_Pred = (x: any) => boolean;


export type T_PredOf<T> = (x: T) => boolean;


export type T_NumPair = [number, number];


export type T_ReporteEscalasLyr = {
  escalas: Array<T_EscalaLyr>,
  friends: Array<T_ElemLyrPng>
};



export function find_escala_lyr(report: T_ReporteEscalasLyr, png_path: string): ?T_EscalaLyr {
  if (! png_path) {
    return null;
  }
  
  const friend = report.friends.find(elp => elp[2] === takeFilename(png_path));
  
  if (friend != null) {
    const lyr_filename = friend[1];
    
    return report.escalas.find(esc => takeFilename(esc.pathEscala) === lyr_filename);
  }
  else {
    return null;
  }
}


export type T_Formatter = (number => string);



export type T_NumTransformer = (number => number);



export type T_TifPngPairs = {
  [string]: [string, string]             // ejemplo: {B: [b.tif, b.png], pH: [ph.tif, ph.png]}
};



export type T_Friends = {
  elem: string,                // nombre del elemento
  tif : string,                // filename del tif
  png : string                 // filename del png
};


export type T_NextFriends = (trio: ?T_Friends) => void;



export function find_friends(pairs: T_TifPngPairs, png_file: string): ?T_Friends {
  const elem = _.findKey(pairs, tp => tp[1] === png_file);
  
  if (elem != null) {
    return {elem, tif: pairs[elem][0], png: pairs[elem][1]};
  }
  else {
    return null;
  }
}



export type T_AreasZonas = {
  areas: Array<number>,
  zonas: Array<number>
};


export type T_ErrAreasZonas = {error: string} | T_AreasZonas;


export type T_NextShpAreas = (x: T_ErrAreasZonas) => void;


export type T_ResultMeasure = {
  kind1  : string,
  result1: string,
  kind2  : string,
  result2: string
};


export const NO_MEASURE: T_ResultMeasure = {kind1: 'none', result1: '', kind2: 'none', result2: ''};


export type T_MinMax = [number, number];


export type T_MinMaxNext = (x: T_MinMax) => void;


export type T_PngUnits = {
  [string]: [string]
};


export type T_NextPngUnits = (x: T_PngUnits) => void;


export type T_Obs = {
  titulo : string,
  fecha  : string,
  user   : string,
  lat    : number,
  lng    : number,
  cambios: boolean,
  nota   : string,
  path   : string
}



export type T_Fun = () => void;



export function empty_obs(dir: string, user: T_User, lat: number, lng: number): T_Obs {
  const fecha = moment().toJSON(),
        path  = `${strip_end_slash(dir)}/${fecha}.obs`;
  
  return {
    titulo : '',
    fecha,
    user   : user.user_name,
    lat,
    lng,
    cambios: true,
    nota   : '',
    path
  };
}



export type T_SenAlerta = {
  id      : number,
  nombre  : string,
  email   : string,
  temp_ref: number,
  temp_opt: string,
  hum_ref : number,
  hum_opt : string,
  estado  : boolean
};



export type T_Sen = {
  id     : number,
  uuid   : string,
  titulo : string,
  fecha  : string,
  lat    : number,
  lng    : number,
  dir    : string,
  file   : string,
  path   : string,
  alertas: Array<T_SenAlerta>
};



export function empty_sen(dir: string, lat: number, lng: number): T_Sen {
  const fecha = moment().toJSON();

  return {
    id      : 0,
    uuid    : uuidv1(),
    titulo  : 'Sensor',
    fecha,
    lat,
    lng,
    dir,
    file    : '',
    path    : '',
    alertas : [{
      id      :    -1,
      nombre  :    '',
      email   :    '',
      temp_ref:     0,
      temp_opt:    '',
      hum_ref :     0,
      hum_opt :    '',
      estado  : false
    },{
      id      :    -1,
      nombre  :    '',
      email   :    '',
      temp_ref:     0,
      temp_opt:    '',
      hum_ref :     0,
      hum_opt :    '',
      estado  : false
    }]
  };
}

export function empty_alerta(): T_SenAlerta {
  return {
    id      :    -1,
    nombre  :    '',
    email   :    '',
    temp_ref:     0,
    temp_opt:    '',
    hum_ref :     0,
    hum_opt :    '',
    estado  : false
  }
}



export type T_IconPair = {
  normal : string,
  hilight: string
};


export type T_Paths = Array<string>;


export type T_ErrPaths = {error: string} | Array<string>;


export type T_Date = {
  d: number,
  m: number,
  y: number
};


export type T_NextDate = (x: T_Date) => void;


export function date_string(date: T_Date): string {
  return `${date.d}/${date.m}/${date.y}`;
}



export type T_MapnikMethods = 'near' | 'bilinear';



export type T_ReportEntry = {
  Path      : string,
  Programa  : string,
  Agricultor: string,
  Predio    : string,
  Periodo   : string,
  Cultivable: string,
  Servicio  : string,
  MS        : string,
  MS_pts    : number,
  Especies  : Array<string>,
  Area      : number,
  Lat       : number,
  Long      : number
};



export type T_ColOrd = {
  col: string,
  ord: string
};



export type T_UserMin = {
  user_name: string,
  role     : T_Role,
  true_dirs: Array<string>,
};


export type T_UsersMinErr = Array<T_UserMin> | T_Err;


export type T_UserErr = T_User | T_Err;


export type T_PredUserMin = (user: T_UserMin) => boolean;


export function bad_min_user(user: T_UserMin): boolean {
  return (
    (! user.true_dirs) ||
    (! user.role) ||
    (user.true_dirs.length === 0) ||
    (user.true_dirs.length !== _.uniq(user.true_dirs).length) ||
    (user.role !== AsesorExterno && Role.known.indexOf(user.role) === -1)
  );
}



export function to_min_user(user: T_User): T_UserMin {
  return {
    user_name: user.user_name,
    role     : user.role,
    true_dirs: user.true_dirs
  };
}



export type T_TrimResultOk = {
  ok         : string,
  need_recalc: boolean
};



export type T_TrimResultErr = {
  error: string,
  total: number,
  bads : Array<string>
};



export type T_TrimResult = T_TrimResultOk | T_TrimResultErr;



export type T_Png_Culti = {
  png  : string,
  culti: string
};



export type T_Extent = {
  size      : [number, number],
  upperLeft : [number, number],
  lowerRight: [number, number]
};



export type T_CollectPoint = {
  id         : number,
  found      : boolean,
  agricultor : string,
  user_name  : string,
  path_predio: string,       // path del predio, o sea, un nivel antes de la carpeta collect/
  fecha      : string,
  fmoment    : Object,       // objeto moment de la fecha
  funix      : number,       // fecha en formato unix (segundos desde 1/1/1970)
  nota       : string,
  foto       : string,
  lat        : number,
  lon        : number,
  alt        : number,
  manejos    : Array<string>
};



export type T_CollectFolder = {
  path_collect: string,              // path completo de la carpeta collect/ donde viven los points
  points      : Array<T_CollectPoint>
};



export type T_NextExtent = (?T_Extent) => void;



export type T_UnitInv = {
  units   : string,
  inverted: boolean
};



export type T_NextUnitInvErr = (result: T_UnitInv | string) => void;



export type T_FertCantidad = {
  lote    : number,
  ha      : number,
  dosis   : number,
  cantidad: number
};



export type T_DosisMin = {
  id: number,        // id del potrero
  n : string,
  p : string,
  k : string,
  ca: string,
  s : string,
  mg: string,
  b : string,
  zn: string
};



export type T_DemSum = {
  elem_name : string,
  demanda   : number,
  suministro: number
};



export type T_DemSumHas = {
  elem_name : string,
  demanda   : number,
  suministro: number,
  hectareas : number
};



export type T_Mezcla = {
  lote   : number,
  formula: string,
  mezcla : {
    [string]: number
  }
};



export type T_Fert = {
  especie             : string,
  metodo_riego        : string,
  product             : string,
  Consid_Solida       : string,
  Consid_Fertirriego  : string,
  Consid_Foliar       : string,
  cantidades          : Array<T_FertCantidad>,
  dosis               : Array<T_DosisMin>,
  demandas_suministros: Array<T_DemSum>,    // NOTE: demandas y suministros son promedios ponderados por hectáreas.
  mezclas             : Array<T_Mezcla>
};



export type T_RowEspecieCR = {
  Especie: string,
  Nombre_alternativo: string,
  sinonimos: Array<string>,
  Unidad_rendimiento: string,
  Consid_Unidad_rendimiento: string,
  Nivel_pH: string,
  rdto_min: string,
  rdto_max: string,
  RIN: string,
  RIP: string,
  RIK: string,
  RI_Ca: string,
  RI_Mg: string,
  RI_S: string,
  RI_Zn: string,
  RI_B: string,
  Demanda_Minima_N: string,
  Demanda_Maxima_N: string,
  Demanda_Minima_P2O5: string,
  Demanda_Maxima_P2O5: string,
  Demanda_Minima_K2O: string,
  Demanda_Maxima_K2O: string,
  Demanda_Minima_CaO: string,
  Demanda_Maxima_CaO: string,
  Demanda_Minima_MgO: string,
  Demanda_Maxima_MgO: string,
  Demanda_Minima_S: string,
  Demanda_Maxima_S: string,
  Demanda_Minima_Zn: string,
  Demanda_Maxima_Zn: string,
  Demanda_Minima_B: string,
  Demanda_Maxima_B: string,
  Consid_Solida: string,
  Consid_Fertirriego: string,
  Consid_Foliar: string
};



// WEBPACK FOOTER //
// ./src/types.js